/**
 * Browser host for the liveness enrolment flow.
 *
 * All sequencing rules live in `core/enrolment/livenessChallenge.ts`; this supplies a camera,
 * a pose estimate and a smile score, and turns the resulting status into React state.
 *
 * The captured frames are held **in memory only** and handed back as JPEG blobs for the caller
 * to POST to the embedding service. Nothing is written to disk, and the frames are released
 * when the hook unmounts.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  LivenessSession,
  PnPHeadPoseEstimator,
  SystemClock,
  drawChallenges,
  type ChallengeKind,
  type FaceLandmarks,
  type LivenessStatus,
  type Policy,
} from '../core/index.js';

import { createFaceLandmarker, detectLandmarks, type LandmarkerHandle } from '../cv/faceLandmarker';
import { alignFaceToJpeg } from '../cv/faceAlign';
import { browserPolicy } from '../lib/policy';

export type EnrolmentPhase = 'idle' | 'starting' | 'running' | 'passed' | 'failed';

export interface CapturedFrame {
  /** Which challenge produced it, or `frontal` for a template frame. */
  readonly challenge: ChallengeKind | 'frontal';
  readonly blob: Blob;
  readonly capturedAt: string;
}

export interface UseLivenessEnrolmentResult {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  phase: EnrolmentPhase;
  status: LivenessStatus | null;
  /** Frames captured at the moment each challenge was satisfied. */
  frames: readonly CapturedFrame[];
  errorMessage: string | undefined;
  policy: Policy;
  /** Challenge order in play. In production this must come from the server. */
  challenges: readonly ChallengeKind[];
  start: () => void;
  retry: () => void;
}

export interface UseLivenessEnrolmentOptions {
  /**
   * Server-issued challenge order.
   *
   * When omitted the hook draws its own, which is acceptable only for local testing: a client
   * that picks its own challenges can pick a sequence it has already recorded.
   */
  serverChallenges?: readonly ChallengeKind[];
  onComplete?: (frames: readonly CapturedFrame[]) => void;
}

export function useLivenessEnrolment(
  options: UseLivenessEnrolmentOptions = {},
): UseLivenessEnrolmentResult {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [phase, setPhase] = useState<EnrolmentPhase>('idle');
  const [status, setStatus] = useState<LivenessStatus | null>(null);
  const [frames, setFrames] = useState<CapturedFrame[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [runToken, setRunToken] = useState(0);
  const [active, setActive] = useState(false);

  const resolved = useMemo(() => browserPolicy(), []);
  const policy = resolved.policy;

  /**
   * Stable identity for the requested challenge order.
   *
   * Keyed on the *contents* rather than the array reference on purpose. Memoising on the
   * reference meant a caller writing the natural `serverChallenges: ['smile']` handed us a new
   * array on every render, which recomputed `challenges`, which is a dependency of the capture
   * effect below — so the effect tore down and restarted, stopping and restarting the camera.
   * With the loop calling `setStatus` about ten times a second, that became a continuous
   * on/off cycle and re-authentication could never complete. Comparing by content makes the
   * hook immune to how the caller spells the argument.
   */
  const challengeKey = options.serverChallenges?.join(',') ?? '';

  const challenges = useMemo<readonly ChallengeKind[]>(
    () =>
      options.serverChallenges === undefined
        ? drawChallenges(policy.enrolment.challengeCount)
        : [...options.serverChallenges],
    // runToken so a retry draws a fresh order rather than repeating the one just failed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [challengeKey, policy.enrolment.challengeCount, runToken],
  );

  const start = useCallback(() => {
    setFrames([]);
    setStatus(null);
    setErrorMessage(undefined);
    setPhase('starting');
    setActive(true);
  }, []);

  const retry = useCallback(() => {
    setRunToken((token) => token + 1);
    start();
  }, [start]);

  useEffect(() => {
    if (!active) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let stream: MediaStream | null = null;
    let landmarker: LandmarkerHandle | null = null;

    const clock = new SystemClock();
    const estimator = new PnPHeadPoseEstimator({
      maxReprojectionErrorPx: policy.vision.maxReprojectionErrorPx,
    });
    const session = new LivenessSession({
      challenges,
      policy: policy.enrolment,
      thresholds: policy.thresholds,
    });

    const captured: CapturedFrame[] = [];
    let captureCount = 0;

    /**
     * Capture an ArcFace-aligned tile for the embedding call.
     *
     * Not a raw frame: the recognition model needs the eyes, nose and mouth on fixed pixel
     * positions, and the embedding service does not provide that. Returns quietly when the
     * landmarks cannot be aligned, so a distorted tile is never enrolled.
     */
    const captureFrame = async (
      challenge: ChallengeKind | 'frontal',
      landmarks: FaceLandmarks,
    ): Promise<void> => {
      const video = videoRef.current;
      if (video === null) return;
      const aligned = await alignFaceToJpeg(video, landmarks, {
        streamIsMirrored: policy.camera.streamIsMirrored,
      });
      if (aligned === null) return;
      captured.push({ challenge, blob: aligned.blob, capturedAt: new Date().toISOString() });
    };

    const tick = async (): Promise<void> => {
      if (cancelled) return;
      const video = videoRef.current;
      if (video === null || landmarker === null) return;

      if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && video.videoWidth > 0) {
        const monotonicTs = clock.monotonic();
        const detection = detectLandmarks(
          landmarker,
          video,
          monotonicTs * 1000,
          policy.vision.presenceConfidence,
          policy.camera.streamIsMirrored,
        );

        const estimate =
          detection.landmarks === null
            ? null
            : estimator.estimate(detection.landmarks, policy.vision.presenceConfidence);

        const next = session.observe({
          monotonicTs,
          pose: estimate?.pose ?? null,
          smileScore: detection.smileScore,
          faceDetected: detection.landmarks !== null,
          faceCount: detection.landmarks?.faceCount ?? 0,
          confidence: estimate?.confidence ?? 0,
        });

        // Capture the *template* frames, not the challenge frames.
        //
        // The challenge frames are at deliberate extremes (left profile, right profile, chin
        // up). Averaging those into a template produces a centroid that resembles no actual
        // pose, and every later frontal comparison then scores below threshold — which is
        // exactly the "same person, different angle, reported as a mismatch" failure. The
        // challenges prove a live human; these near-frontal frames describe their face.
        if (next.templateTimestamps.length > captureCount && detection.landmarks !== null) {
          captureCount = next.templateTimestamps.length;
          await captureFrame('frontal', detection.landmarks);
        }

        setStatus(next);

        if (next.passed) {
          setFrames([...captured]);
          setPhase('passed');
          setActive(false);
          options.onComplete?.(captured);
          return;
        }
        if (next.failed) {
          setFrames([]);
          setPhase('failed');
          setActive(false);
          return;
        }
      }

      if (!cancelled) timer = setTimeout(() => void tick(), 1000 / policy.vision.targetProcessFps);
    };

    const begin = async (): Promise<void> => {
      try {
        if (navigator.mediaDevices?.getUserMedia === undefined) {
          throw new Error('This browser does not support camera access (getUserMedia).');
        }
        // Wait for the <video> element to mount.
        for (let i = 0; i < 60 && videoRef.current === null; i += 1) {
          await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
        }
        const video = videoRef.current;
        if (video === null) throw new Error('Camera preview could not mount — reload and retry.');

        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: policy.camera.width },
            height: { ideal: policy.camera.height },
            facingMode: 'user',
          },
          audio: false,
        });
        if (cancelled) return;

        video.srcObject = stream;
        video.playsInline = true;
        video.muted = true;
        await video.play();

        landmarker = await createFaceLandmarker(policy.vision);
        if (cancelled) {
          landmarker.close();
          return;
        }

        setPhase('running');
        timer = setTimeout(() => void tick(), 0);
      } catch (error) {
        if (cancelled) return;
        setPhase('failed');
        setErrorMessage(friendlyError(error));
        setActive(false);
        stream?.getTracks().forEach((track) => track.stop());
      }
    };

    void begin();

    return () => {
      cancelled = true;
      if (timer !== undefined) clearTimeout(timer);
      landmarker?.close();
      stream?.getTracks().forEach((track) => track.stop());
      if (videoRef.current !== null) videoRef.current.srcObject = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, challenges, policy]);

  return {
    videoRef,
    phase,
    status,
    frames,
    errorMessage,
    policy,
    challenges,
    start,
    retry,
  };
}

function friendlyError(error: unknown): string {
  if (error instanceof DOMException) {
    switch (error.name) {
      case 'NotAllowedError':
      case 'PermissionDeniedError':
        return 'Camera permission was denied. Allow camera access for this site, then retry.';
      case 'NotFoundError':
        return 'No camera was found on this device.';
      case 'NotReadableError':
        return 'The camera is in use by another application. Close it and retry.';
      default:
        return error.message || error.name;
    }
  }
  if (error instanceof Error) return error.message;
  return 'Enrolment could not start.';
}
